import { ActionIcon, Flex, Text, ThemeIcon, Tooltip } from '@mantine/core';
import { IconEdit, IconRefresh, IconSparkles, IconTrash } from '@tabler/icons-react';
import cn from 'classnames';
import { observer } from 'mobx-react-lite';
import * as React from 'react';

import BaseClickable from '@/components/ui/BaseClickable';
import { PdfDocumentNote } from '@/stores/local/shared/PdfNotesStore';
import { formatDate } from '@/utils/formatDate';
import { markdownPlainPreview } from '@/utils/markdownPlainPreview';

import s from './DocumentNoteListItem.module.scss';

type DocumentNoteListItemProps = {
  note: PdfDocumentNote;
  isActive: boolean;
  onOpen: (noteId: string) => void;
  onEdit: (noteId: string) => void;
  onDelete: (noteId: string) => void;
  /** Для ИИ-саммари: обновить сводку (иконка в строке действий). */
  onRegenerate?: () => void;
};

const DocumentNoteListItem: React.FC<DocumentNoteListItemProps> = ({
  note,
  isActive,
  onOpen,
  onEdit,
  onDelete,
  onRegenerate,
}) => {
  const isAiSummary = Boolean(note.isAiDocumentSummary);

  const handleEdit = React.useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      event.stopPropagation();
      onEdit(note.id);
    },
    [note.id, onEdit],
  );

  const handleDelete = React.useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      event.stopPropagation();
      onDelete(note.id);
    },
    [note.id, onDelete],
  );

  const handleRegenerate = React.useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      event.stopPropagation();
      onRegenerate?.();
    },
    [onRegenerate],
  );

  return (
    <BaseClickable
      element="div"
      className={cn(s.root, isActive && s.root_active, isAiSummary && s['root_ai-summary'])}
      onClickWithValue={onOpen}
      value={note.id}
      ariaLabel={isAiSummary ? 'Открыть саммари по заметкам' : 'Открыть заметку'}
    >
      <Flex justify="space-between" align="flex-start" gap="xs" className={s.root__header}>
        <Flex direction="column" gap={6} className={s.root__meta}>
          {isAiSummary ? (
            <Flex align="flex-start" gap={8} className={s['root__title-row']}>
              <Tooltip label="Сводка по вашим заметкам с помощью ИИ" withArrow position="top">
                <ThemeIcon size="sm" variant="light" color="indigo" className={s['root__ai-icon']} aria-hidden>
                  <IconSparkles size={14} />
                </ThemeIcon>
              </Tooltip>
              <Text fw={600} size="sm" lineClamp={2} className={s.root__caption}>
                {note.caption}
              </Text>
            </Flex>
          ) : (
            <Text fw={600} size="sm" lineClamp={1}>
              {note.caption}
            </Text>
          )}
        </Flex>
        <Flex align="center" gap={4} className={s.root__actions}>
          {!isAiSummary && (
            <ActionIcon
              variant="subtle"
              size="sm"
              onClick={handleEdit}
              aria-label="Редактировать заметку"
              className={s.root__action}
            >
              <IconEdit size={14} />
            </ActionIcon>
          )}
          {isAiSummary && onRegenerate && (
            <Tooltip label="Обновить саммари по заметкам" withArrow position="top">
              <ActionIcon
                variant="subtle"
                size="sm"
                onClick={handleRegenerate}
                aria-label="Обновить саммари по заметкам к документу"
                className={s.root__action}
              >
                <IconRefresh size={14} />
              </ActionIcon>
            </Tooltip>
          )}
          {!isAiSummary && (
            <ActionIcon
              variant="subtle"
              size="sm"
              onClick={handleDelete}
              aria-label="Удалить заметку"
              className={s.root__action}
            >
              <IconTrash size={14} />
            </ActionIcon>
          )}
        </Flex>
      </Flex>
      <Text size="sm" className={s.root__preview}>
        {markdownPlainPreview(note.text)}
      </Text>
      <Flex justify="flex-end" className={s.root__footer}>
        <Text size="xs" c="dimmed">
          {isAiSummary
            ? `Обновлено: ${formatDate(note.updatedAt)}`
            : `стр. ${note.pageNumber} · ${formatDate(note.updatedAt)}`}
        </Text>
      </Flex>
    </BaseClickable>
  );
};

export default observer(DocumentNoteListItem);
